/**
 * TanStack Query hooks for the reference-docs admin API.
 *
 * Backend routes: `apps/api/flow_api/routers/reference_docs.py`.
 * Supervisors upload SOP / CPT / payer cheat-sheet files here; the
 * agent runtime fetches content via `list_reference_docs` /
 * `fetch_reference_doc` tool calls at workflow-run time.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/apiClient";
import { acquireAccessToken } from "@/lib/auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export interface ReferenceDoc {
  id: string;
  entityCode: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  tags: string[];
  summary: string | null;
  hasExtractedText: boolean;
  uploadedAt: string;
  updatedAt: string;
}

export interface ReferenceDocsList {
  items: ReferenceDoc[];
  total: number;
}

export interface UploadReferenceDocPayload {
  entityCode: string;
  tags?: string;
  file: File;
}

export interface UpdateReferenceDocPayload {
  tags?: string;
  summary?: string;
}

const QK = {
  list: (entity?: string, tag?: string) =>
    ["reference-docs", "list", entity ?? "all", tag ?? ""] as const,
  single: (id: string) => ["reference-docs", "single", id] as const,
  content: (id: string) => ["reference-docs", "content", id] as const,
};

export function useReferenceDocs(params?: { entity?: string; tag?: string }) {
  return useQuery<ReferenceDocsList>({
    queryKey: QK.list(params?.entity, params?.tag),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.entity) qs.set("entity", params.entity);
      if (params?.tag) qs.set("tag", params.tag);
      const tail = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch(`/reference-docs${tail}`);
    },
  });
}

export function useUploadReferenceDoc() {
  const qc = useQueryClient();
  return useMutation<ReferenceDoc, Error, UploadReferenceDocPayload>({
    mutationFn: async (payload) => {
      // Multipart uploads have to bypass apiFetch — we need fetch to
      // auto-set Content-Type with a boundary, which apiFetch's
      // default header insertion overrides.
      const token = await acquireAccessToken();
      const form = new FormData();
      form.set("entity_code", payload.entityCode);
      form.set("tags", payload.tags ?? "");
      form.set("file", payload.file);
      const response = await fetch(`${API_BASE_URL}/reference-docs`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const correlationId = response.headers.get("X-Correlation-ID");
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let message = text || response.statusText;
        try {
          const parsed = JSON.parse(text) as { detail?: string };
          if (typeof parsed.detail === "string") message = parsed.detail;
        } catch {
          // not JSON
        }
        throw new ApiError(message, response.status, correlationId);
      }
      return (await response.json()) as ReferenceDoc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reference-docs", "list"] });
    },
  });
}

export function useUpdateReferenceDoc(id: string) {
  const qc = useQueryClient();
  return useMutation<ReferenceDoc, Error, UpdateReferenceDocPayload>({
    mutationFn: (payload) =>
      apiFetch(`/reference-docs/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reference-docs", "list"] });
      qc.invalidateQueries({ queryKey: QK.single(id) });
    },
  });
}

export function useDeleteReferenceDoc() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiFetch(`/reference-docs/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reference-docs", "list"] });
    },
  });
}

export function useReferenceDocContent(id: string | null) {
  return useQuery<string>({
    queryKey: id ? QK.content(id) : ["reference-docs", "content", "none"],
    queryFn: async () => {
      if (!id) return "";
      const token = await acquireAccessToken();
      const response = await fetch(`${API_BASE_URL}/reference-docs/${id}/content`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new ApiError(text || response.statusText, response.status, null);
      }
      return await response.text();
    },
    enabled: Boolean(id),
  });
}
